import type { DeepKeys, DeepValue, Updater } from "@tanstack/react-form";
import type { z } from "zod";

import type { FieldInputProps } from "@/components/form/field-input";
import InsideLabelField from "@/components/form/inside-label-field";
import { Input } from "@/components/ui/input";
import { getOnChangeFromZod, handleMinMax } from "@/utils/form";

const FieldInput = <
  TFormData,
  TName extends DeepKeys<TFormData> & string,
  TSchema extends z.ZodType<TFormData> | undefined,
  TData extends DeepValue<TFormData, TName> = DeepValue<TFormData, TName>,
>({
  Field,
  name,
  schema,
  label,
  onChange,
  validators,
  labelProps,
  gridRow,
  ...props
}: FieldInputProps<TFormData, TName, TSchema, TData>) => {
  return (
    <Field
      name={name}
      validators={{
        onChange: schema ? getOnChangeFromZod(schema, name) : undefined,
        ...(validators as any), // unsolved typing issue
      }}
      children={(field) => {
        return (
          <InsideLabelField
            errors={field.state.meta.errors}
            gridRow={gridRow}
            {...labelProps}
          >
            <Input
              insideLabel={
                <label
                  htmlFor={name}
                  className="absolute text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-3 scale-75 top-4 z-10 origin-[0] start-3.5 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-3 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto"
                >
                  {label}
                </label>
              }
              placeholder=""
              className="px-3.5 pb-1.5 pt-5 h-[55px] peer"
              id={name}
              value={(field.state.value as string | null)?.toString() ?? ""}
              onBlur={() => {
                if (props.type === "number") {
                  (
                    field.handleChange as (
                      updater: Updater<string | number | null>
                    ) => void
                  )(
                    handleMinMax(
                      field.state.value as number | null,
                      props.min,
                      props.max
                    )
                  );
                }
                field.handleBlur();
              }}
              onChange={(e) => {
                if (onChange) onChange(e);
                const value = e.target.value;
                (
                  field.handleChange as (
                    updater: Updater<string | number | null>
                  ) => void
                )(
                  props.type === "number"
                    ? value !== ""
                      ? handleMinMax(value, props.min, props.max)
                      : null
                    : value
                );
              }}
              errors={!!field.state.meta.errors?.length}
              {...props}
            />
          </InsideLabelField>
        );
      }}
    />
  );
};

export default FieldInput;
